/**
 * `fetch` do client Supabase que renova a sessão quando o SERVIDOR diz que o JWT
 * venceu — GUPM-336.
 *
 * Rede de segurança que não depende do relógio do PC: o auth-js só renova quando
 * `Date.now()` diz que venceu, e numa estação atrasada isso não acontece. Aqui quem
 * decide é o PostgREST (`401 PGRST303`). Renova uma vez (compartilhada entre todas as
 * requisições que falharem juntas) e repete a requisição uma vez.
 *
 * Só `/rest/v1/`: repetir uma Edge Function reenviaria o código TOTP e gastaria
 * tentativa do lockout; o `/auth/v1/` é o próprio caminho da renovação.
 */
import { ehJwtExpirado } from '@/domain/sessao/relogio';

/** Devolve o access token novo, ou `null` se não deu para renovar. */
export type Renovar = () => Promise<string | null>;

function urlDe(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

async function corpoJson(r: Response): Promise<unknown> {
  try {
    return await r.clone().json();
  } catch {
    return null;
  }
}

export function criarFetchComRenovacao(fetchBase: typeof fetch, renovar: Renovar): typeof fetch {
  let emCurso: Promise<string | null> | null = null;
  /** Última troca feita: um 401 atrasado que ainda leva o token `de` só precisa do `para`. */
  let ultimaTroca: { de: string | null; para: string } | null = null;

  /** Uma renovação por rodada: quem chegar enquanto ela corre espera a mesma. */
  function renovarUmaVez(tokenVencido: string | null): Promise<string | null> {
    if (ultimaTroca && ultimaTroca.de === tokenVencido) return Promise.resolve(ultimaTroca.para);
    emCurso ??= renovar()
      .catch(() => null)
      .then((novo) => {
        if (novo) ultimaTroca = { de: tokenVencido, para: novo };
        return novo;
      })
      .finally(() => {
        emCurso = null;
      });
    return emCurso;
  }

  return async (input, init) => {
    const resposta = await fetchBase(input, init);
    if (resposta.status !== 401 || !urlDe(input).includes('/rest/v1/')) return resposta;
    if (!ehJwtExpirado(resposta.status, await corpoJson(resposta))) return resposta;

    const token = await renovarUmaVez(new Headers(init?.headers).get('Authorization'));
    if (!token) {
      console.warn('[sessao] JWT vencido e a renovação falhou — devolvendo o 401');
      return resposta;
    }
    console.warn('[sessao] JWT vencido no servidor — sessão renovada e requisição repetida');
    const headers = new Headers(init?.headers);
    headers.set('Authorization', `Bearer ${token}`);
    return fetchBase(input, { ...init, headers });
  };
}
